<script setup lang="ts">
    import { RecentItem } from '../DashboardTypes';
    import { formatDateTime, formatDate } from '@shared/utils/formatDateTime';
    import { formatNumber } from '@shared/utils/formatNumber';
    import EventListItemFullStatus from '@views/Event/EventListItemFullStatus.vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { DashboardAccess } from '@views/Dashboard/Dashboard.access';
    import { inject } from 'vue';
    import IssueSubject from '@cwd/Views/Issues/IssueSubject.vue';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const dashboardAccess: DashboardAccess = injectorAccess.injector.get(DashboardAccess);

    const props = defineProps<{ item: RecentItem }>();
</script>

<template>
    <template v-if="item.Type === 'Issue'">
        <h4 class="item-heading">
            <a :href="item.Url">
                <IssueSubject :subject="item.Title" :dynamic-subject="item.DynamicSubject" />
            </a>
            <button
                type="button"
                class="btn btn-subtle btn-xs"
                v-tooltip.top="$Resources.IssuePreviewButton"
                @click="dashboardAccess.previewObject(item.Url.replace('/Statistics', '') + '?preview=true')">
                <span class="glyphicon glyphicon-eye-open" aria-hidden="true"></span>
            </button>
        </h4>
        <div class="ug-bars row m-sm-t m-sm-b">
            <div class="col-lg-3 col-md-6 col-sm-6 col-xs-6">
                <div class="bar-label">
                    {{ $Resources.IssuesSentLabel }}
                    <span>{{ formatNumber(item.SentCount) }}</span>
                </div>
                <div class="bar">
                    <span :style="{ width: item.SentPercentage + '%' }" class="bar-value bg-sent"></span>
                </div>
            </div>
            <div class="col-lg-3 col-md-6 col-sm-6 col-xs-6">
                <div class="bar-label">
                    {{ $Resources.IssuesReceivedPercentageLabel }}
                    <span>{{ formatNumber(item.ReceivedPercentage) }}%</span>
                </div>
                <div class="bar">
                    <span :style="{ width: item.ReceivedPercentage + '%' }" class="bar-value bg-received"></span>
                </div>
            </div>
            <div class="col-lg-3 col-md-6 col-sm-6 col-xs-6">
                <div class="bar-label">
                    {{ $Resources.IssuesOpenedPercentageLabel }}
                    <span>{{ formatNumber(item.OpenPercentage) }}%</span>
                </div>
                <div class="bar">
                    <span :style="{ width: item.OpenPercentage + '%' }" class="bar-value bg-opened"></span>
                </div>
            </div>
            <div class="col-lg-3 col-md-6 col-sm-6 col-xs-6">
                <div class="bar-label">
                    {{ $Resources.IssuesClickedPercentageLabel }}
                    <span>{{ formatNumber(item.ClickPercentage) }}%</span>
                </div>
                <div class="bar">
                    <span :style="{ width: item.ClickPercentage + '%' }" class="bar-value bg-clicked"></span>
                </div>
            </div>
        </div>
        <p class="meta">
            <span class="m-md-r">
                <span class="glyphicon glyphicon-envelope"></span>
                {{ $Resources.ItemTypeIssue }}
            </span>
            <span class="m-md-r">
                <span class="glyphicon glyphicon-time"></span>
                <span v-text="formatDateTime(item.Date)"></span>
            </span>
            <span class="m-md-r" v-if="item.LastModifiedBy">
                <span class="glyphicon glyphicon-user"></span>
                <span v-text="item.LastModifiedBy.Name"></span>
            </span>
        </p>
    </template>

    <template v-if="item.Type === 'Survey' || item.Type === 'ActiveSurvey'">
        <h4>
            <a :href="item.Url" v-text="item.Title ? item.Title : $Resources.NoTitlePlaceholder"></a>
            <button
                type="button"
                class="btn btn-subtle btn-xs m-0-b"
                v-tooltip.top="$Resources.IssuePreviewButton"
                @click="dashboardAccess.previewObject(item.Url.replace('/Results', '') + '?preview=true')">
                <span class="glyphicon glyphicon-eye-open" aria-hidden="true"></span>
            </button>
        </h4>
        <div class="ug-bars row m-sm-t m-sm-b">
            <div class="col-lg-6 col-md-12 col-sm-12" v-if="item.Type === 'Survey'">
                <div class="bar-label">
                    {{ $Resources.SurveyEndDateLabel }}
                    <span>{{ formatDate(item.Date) }}</span>
                </div>
                <div class="bar"></div>
            </div>
            <div class="col-lg-6 col-md-12 col-sm-12" v-else>
                <div class="bar-label">
                    {{ $Resources.SurveyStartedLabel }}
                    <span>{{ formatDate(item.Date) }}</span>
                </div>
                <div class="bar"></div>
            </div>
            <div class="col-lg-3 col-md-6 col-sm-6 col-xs-6">
                <div class="bar-label">
                    {{ $Resources.SurveyQuestionCountLabel }}
                    <span>{{ formatNumber(item.QuestionCount) }}</span>
                </div>
                <div class="bar"></div>
            </div>
            <div class="col-lg-3 col-md-6 col-sm-6 col-xs-6">
                <div class="bar-label">
                    {{ $Resources.SurveyAnswerCountLabel }}
                    <span>{{ formatNumber(item.AnswerCount) }}</span>
                </div>
                <div class="bar"></div>
            </div>
        </div>
        <p class="meta">
            <span class="m-md-r">
                <span class="glyphicon glyphicon-th-list"></span>
                {{ $Resources.ItemTypeSurvey }}
            </span>
            <span class="m-md-r">
                <span class="glyphicon glyphicon-time"></span>
                <span v-text="formatDateTime(item.Date)"></span>
            </span>
            <span class="m-md-r" v-if="item.LastModifiedBy">
                <span class="glyphicon glyphicon-user"></span>
                <span v-text="item.LastModifiedBy.Name"></span>
            </span>
        </p>
    </template>

    <template v-if="item.Type === 'Event2Dto'">
        <h4>
            <a :href="item.Url" v-text="item.Title ? item.Title : $Resources.NoTitlePlaceholder"></a>
            <button
                type="button"
                class="btn btn-subtle btn-xs m-0-b"
                v-tooltip.top="$Resources.IssuePreviewButton"
                @click="dashboardAccess.previewObject(item.Url + '?preview=true')">
                <span class="glyphicon glyphicon-eye-open" aria-hidden="true"></span>
            </button>
        </h4>
        <EventListItemFullStatus :eventId="item.ObjectId" />
        <p class="meta">
            <span class="m-md-r">
                <span class="glyphicon glyphicon-calendar"></span>
                {{ $Resources.ItemTypeEvent }}
            </span>
            <span class="m-md-r">
                <span class="glyphicon glyphicon-time"></span>
                <span v-text="formatDateTime(item.Date)"></span>
            </span>
            <span class="m-md-r" v-if="item.LastModifiedBy">
                <span class="glyphicon glyphicon-user"></span>
                <span v-text="item.LastModifiedBy.Name"></span>
            </span>
        </p>
    </template>

    <template v-if="item.Type === 'SmsMessageDto'">
        <h4>
            <a :href="item.Url" v-text="item.Title ? item.Title : $Resources.NoTitlePlaceholder"></a>
        </h4>
        <div class="ug-bars row m-sm-t m-sm-b">
            <div class="col-lg-3 col-md-6 col-sm-6 col-xs-6">
                <div class="bar-label">
                    {{ $Resources.SmsSentLabel }}
                    <span>{{ formatNumber(item.RecipientCount) }}</span>
                </div>
                <div class="bar">
                    <span :style="{ width: '100%' }" class="bar-value bg-sent"></span>
                </div>
            </div>
            <div class="col-lg-3 col-md-6 col-sm-6 col-xs-6">
                <div class="bar-label">
                    {{ $Resources.SmsPendingLabel }}
                    <span>{{ formatNumber(item.PendingPercentageSms) }}%</span>
                </div>
                <div class="bar">
                    <span :style="{ width: item.PendingPercentageSms + '%' }" class="bar-value bg-pending"></span>
                </div>
            </div>
            <div class="col-lg-3 col-md-6 col-sm-6 col-xs-6">
                <div class="bar-label">
                    {{ $Resources.SmsReceivedPercentageLabel }}
                    <span>{{ formatNumber(item.SentPercentage) }}%</span>
                </div>
                <div class="bar">
                    <span :style="{ width: item.SentPercentage + '%' }" class="bar-value bg-received"></span>
                </div>
            </div>
            <div class="col-lg-3 col-md-6 col-sm-6 col-xs-6">
                <div class="bar-label">
                    {{ $Resources.SmsBouncedPercentageLabel }}
                    <span>{{ formatNumber(item.BouncePercentageSms) }}%</span>
                </div>
                <div class="bar">
                    <span :style="{ width: item.BouncePercentageSms + '%' }" class="bar-value bg-bounced"></span>
                </div>
            </div>
        </div>
        <p class="meta">
            <span class="m-md-r">
                <span class="glyphicon glyphicon-phone"></span>
                {{ $Resources.ItemTypeSmsMessage }}
            </span>
            <span class="m-md-r">
                <span class="glyphicon glyphicon-time"></span>
                <span v-text="formatDateTime(item.Date)"></span>
            </span>
            <span class="m-md-r" v-if="item.LastModifiedBy">
                <span class="glyphicon glyphicon-user"></span>
                <span v-text="item.LastModifiedBy.Name"></span>
            </span>
        </p>
    </template>
</template>

<style scoped></style>
